/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Point, TextStyleOptions } from 'pixi.js';
import type { GraphicsModel } from './graphics';
import type { RuntimeType } from './RuntimeType';
import type { HitArea } from './hitArea';
import type { Layout } from './Layout';

export enum ModelType {
  Container = 'Container',
  Sprite = 'Sprite',
  Text = 'Text',
  Graphics = 'Graphics',
  NineSlicePlane = 'NineSlicePlane',
  Layout = 'Layout',
}

export type CommonModel<T extends ModelType> = {
  type: T;
  runtimeType?: RuntimeType | null;
  id: string;
  name: string;
  className: string;
  x: number;
  y: number;
  scale: Point;
  angle: number;
  alpha: number;
  interactive: boolean;
  buttonMode: boolean;
  hitArea?: HitArea;
  visible: boolean;
  parent: string;
  data?: any;
};

export type ContainerModel = CommonModel<ModelType.Container> & {
  type: ModelType.Container;
  pivot: Point;
};

export type SpriteModel = CommonModel<ModelType.Sprite> & {
  type: ModelType.Sprite;
  anchor: Point;
  tint: number;
  blendMode: number;
  texture: string;
};

export type TextModel = CommonModel<ModelType.Text> & {
  type: ModelType.Text;
  anchor: Point;
  blendMode: number;
  text: string;
  localize: boolean;
  maxWidth: number;
  maxHeight: number;
  maxFontSize: number;
  style: TextStyleOptions;
};

export type NineSlicePlaneModel = CommonModel<ModelType.NineSlicePlane> & {
  type: ModelType.NineSlicePlane;
  scale: Point;
  pivot: Point;
  width: number;
  height: number;
  leftWidth: number;
  topHeight: number;
  rightWidth: number;
  bottomHeight: number;
  tint: number;
  blendMode: number;
  texture: string;
};

export type NestedLayoutModel = CommonModel<ModelType.Layout> & {
  path: string;
  handleResize: boolean;
  percentX: number;
  percentY: number;
  pivot: Point;
};

export type AnyModel<T extends ModelType> = T extends ModelType.Container
  ? ContainerModel
  : T extends ModelType.Sprite
    ? SpriteModel
    : T extends ModelType.Text
      ? TextModel
      : T extends ModelType.Graphics
        ? GraphicsModel
        : T extends ModelType.NineSlicePlane
          ? NineSlicePlaneModel
          : T extends ModelType.Layout
            ? NestedLayoutModel
            : never;

export type StateName<T extends Layout> =
  // eslint-disable-next-line @typescript-eslint/naming-convention
  T['mInternalTypes'] extends { StateName: string } ? T['mInternalTypes']['StateName'] : T['StateName'];
